import Accordion from '@material-ui/core/Accordion';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import { MuiThemeProvider, Theme } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import AccountBalanceIcon from '@material-ui/icons/AccountBalanceOutlined';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import DeleteIcon from '@material-ui/icons/Delete';
import { DefaultTheme } from '@material-ui/styles';
import classNames from 'classnames';
import merge from 'deepmerge';
import { ArrowLeftCircleOutline as BackIcon, ArrowRightCircleOutline as ForwardIcon } from 'mdi-material-ui';
import React from 'react';
import { Observe } from 'rewire-core';
import { FormView, WithStyle } from 'rewire-ui';
import { getTimeOffStatusColors } from '../DataTypes';
import timeOffBalancesDialogModel from '../schedule/TimeOffBalancesDialogModel';
import TimeOffBalancesView from '../settings/TimeOffBalancesView';
import InfoPanelContent from '../ui/InfoPanelContent';
import InfoPanelFooter from '../ui/InfoPanelFooter';
import InfoPanelHeader from '../ui/InfoPanelHeader';
import MenuButton from '../ui/MenuButton';
import { withStyles } from '../utility/styles';
import { InfoPanelTimeOffRequestViewModel, ITimeOffDetailFields, ITimeOffDetailRow } from './InfoPanelTimeOffRequestViewModel';
import timeOffModel from './TimeOffModel';

const detailRowStyles = (_theme: Theme) => ({
  root: {
    width: '100%',
    margin: '0px !important',
    gridTemplateColumns: 'repeat(auto-fit, minmax(40px, 1fr))',
    gridGap: '8px',
  },
});

interface IDetailRowProps {
  classes?: any;
  detailRow: ITimeOffDetailRow;
}

type DetailRowProps = WithStyle<ReturnType<typeof detailRowStyles>, IDetailRowProps>;

export const DetailRow = withStyles<IDetailRowProps>(
  detailRowStyles,
  class extends React.PureComponent<DetailRowProps> {
    constructor(props: DetailRowProps) {
      super(props);
    }

    render() {
      const { classes, detailRow } = this.props;

      return (
        <Observe
          render={() => (
            <div className={classNames('content', classes.root)}>
              {Object.keys(detailRow.data).map((detailName: keyof ITimeOffDetailFields) => {
                const detailField = detailRow.data[detailName];
                return <detailField.field.Editor key={detailField.field.name} className={`span${detailField.columns}`} />;
              })}
            </div>
          )}
        />
      );
    }
  },
);

const detailRowsStyles = (theme: Theme) => ({
  detailHeader: {},
  detailsContainer: {
    paddingTop: '10px',
    margin: '5px 0px 25px 0px',
    maxHeight: '230px',
    overflowY: 'auto',
  },
  detailRoot: {
    width: '100%',
  },
  detail: {
    display: 'flex',
    alignItems: 'flex-start',
    marginBottom: '1em',
    '&:last-child': {
      marginBottom: '0px',
    },
  },
  detailRowsMessage: {
    color: theme.palette.error.main,
    marginLeft: '10px',
    fontSize: '0.8em',
  },
  removeDetailButton: {
    marginTop: '0.4em',
    marginRight: '10px',
    '&:hover': {
      color: theme.palette.error.main,
    },
  },
});

interface IDetailRowsProps {
  classes?: any;
  model: InfoPanelTimeOffRequestViewModel;
}

type DetailRowsProps = WithStyle<ReturnType<typeof detailRowsStyles>, IDetailRowsProps>;

function getAvailableBalances(model: InfoPanelTimeOffRequestViewModel) {
  const status: 'requested' | 'approved' | 'other' =
    model.timeOffStatus?.toLowerCase() === 'requested'
      ? 'requested'
      : model.timeOffStatus?.toLowerCase() === 'approved'
        ? 'approved'
        : 'other';
  let available = timeOffBalancesDialogModel.available;
  if (available === undefined || status === 'other' || !model.isAllDayRequest || !model.employeeId) return undefined;
  const requested = model.form.field.units.value ?? 0;
  available += !model.isNew && status === 'approved' ? requested : 0;
  const remaining = available - requested;
  return { available, requested, remaining };
}

export const DetailRows = withStyles<IDetailRowsProps>(
  detailRowsStyles,
  class extends React.PureComponent<DetailRowsProps> {
    constructor(props: DetailRowsProps) {
      super(props);
    }

    handleRemoveDetail(id: number) {
      this.props.model.removeTimeOffDetail(id);
    }

    render() {
      return (
        <Observe
          render={() => {
            const { classes, model } = this.props;
            const { isAllDayRequest, detailRowsMessage } = model;

            return isAllDayRequest ? null : (
              <>
                <Typography display="inline" variant="h6" className={classes.detailHeader}>
                  Details
                </Typography>
                <Typography display="inline" className={classes.detailRowsMessage}>
                  {detailRowsMessage}
                </Typography>
                <div className={classes.detailsContainer}>
                  {model.detailRows.map((detailRow: ITimeOffDetailRow) => {
                    return (
                      <div key={detailRow.id} className={classes.detail}>
                        <IconButton
                          className={classes.removeDetailButton}
                          onClick={() => this.handleRemoveDetail(detailRow.id)}
                          disabled={model.viewOnly || (!model.isNew && !model.timeOffIsRequested) || model.isNewRow(detailRow)}
                        >
                          <DeleteIcon />
                        </IconButton>
                        <DetailRow classes={{ root: classes.detailRoot }} detailRow={detailRow} />
                      </div>
                    );
                  })}
                </div>
              </>
            );
          }}
        />
      );
    }
  },
);

const timeOffRequestNavigatorStyles = (_theme: Theme) => ({
  timeOffRequestNavigator: {
    display: 'inline-flex',
    marginLeft: '8px',
    '& > :not(button)': {
      paddingRight: 8,
    },
    alignItems: 'center',
    '& > button': {
      minWidth: 32,
    },
  },
  navigationButton: {
    padding: '2px 8px',
  },
  timeOffRequestNavigatorCounts: {
    paddingLeft: '8px',
  },
});

interface ITimeOffRequestNavigatorProps {
  classes?: any;
  model: InfoPanelTimeOffRequestViewModel;
}

type TimeOffRequestNavigatorProps = WithStyle<ReturnType<typeof timeOffRequestNavigatorStyles>, ITimeOffRequestNavigatorProps>;

export const TimeOffRequestNavigator = withStyles<ITimeOffRequestNavigatorProps>(
  timeOffRequestNavigatorStyles,
  class extends React.PureComponent<TimeOffRequestNavigatorProps> {
    constructor(props: TimeOffRequestNavigatorProps) {
      super(props);
    }

    render() {
      const { classes, model } = this.props;

      return (
        <Observe>
          {() => (
            <div className={classes.timeOffRequestNavigator}>
              <Button
                variant="text"
                className={classes.navigationButton}
                disabled={!model.canNavigateBack()}
                title={model.navigateBackTitle}
                onClick={() => model.navigateBack()}
              >
                <BackIcon />
              </Button>
              <Button
                variant="text"
                className={classes.navigationButton}
                disabled={!model.canNavigateForward()}
                title={model.navigateForwardTitle}
                onClick={() => model.navigateForward()}
              >
                <ForwardIcon />
              </Button>
              <Typography
                variant="h6"
                className={classes.timeOffRequestNavigatorCounts}
              >{`${model.currentTorIdx + 1}/${model.timeOffRequests?.length ?? 0}`}</Typography>
            </div>
          )}
        </Observe>
      );
    }
  },
);

const headerTitleStyles = (theme: Theme) => {
  const timeOffStatusColors = getTimeOffStatusColors(theme);
  return {
    requested: {
      backgroundColor: `${timeOffStatusColors.requested} !important`,
    },
    approved: {
      backgroundColor: `${timeOffStatusColors.approved} !important`,
    },
    onhold: {
      backgroundColor: `${timeOffStatusColors.onhold} !important`,
    },
    declined: {
      backgroundColor: `${timeOffStatusColors.declined} !important`,
    },
    cancelled: {
      backgroundColor: `${timeOffStatusColors.cancelled} !important`,
    },
    published: {
      backgroundColor: `${timeOffStatusColors.published} !important`,
    },
    accepted: {
      backgroundColor: `${timeOffStatusColors.accepted} !important`,
    },
    headerTitleContainer: {
      display: 'flex',
    },
    timeOffStatusButton: {
      marginLeft: '0px',
      marginTop: '5px',
      marginBottom: '5px',
      color: `${theme.palette.common.white} !important`,
      alignSelf: 'flex-start',
      justifyContent: 'center',
    },
    timeOffStatusButtonHasChanges: {
      color: 'rgba(0, 0, 0, 0.26) !important',
      boxShadow: 'none !important',
      backgroundColor: 'rgba(0, 0, 0, 0.12) !important',
    },
  };
};

interface ITimeOffRequestHeaderTitleProps {
  classes?: any;
  model: InfoPanelTimeOffRequestViewModel;
  hideNavigation?: boolean;
}

type TimeOffRequestHeaderTitleProps = WithStyle<ReturnType<typeof headerTitleStyles>, ITimeOffRequestHeaderTitleProps>;

export const TimeOffRequestHeaderTitle = withStyles<ITimeOffRequestHeaderTitleProps>(
  headerTitleStyles,
  class extends React.PureComponent<TimeOffRequestHeaderTitleProps> {
    constructor(props: TimeOffRequestHeaderTitleProps) {
      super(props);
    }

    render() {
      const { classes, model, hideNavigation } = this.props;

      return (
        <Observe
          render={() => {
            const isNew = model.isNew;
            const canEdit = model.timeOffIsRequested;

            return (
              <>
                <div className={this.props.classes.headerTitleContainer}>
                  <Typography variant="h6">{`${isNew ? 'New ' : canEdit ? 'Edit ' : ''}Time Off Request`}</Typography>
                  {!hideNavigation ? <TimeOffRequestNavigator model={model} /> : null}
                </div>
                <Observe
                  render={() => {
                    const timeOffStatusClassName = (model.currentTimeOffRequest?.status || '')
                      .toLocaleLowerCase()
                      .replace(/\s/g, '');
                    return (
                      !model.isNew && (
                        <MenuButton
                          disabled={model.viewOnly || !model.timeOffRequestStatusTransitionItems.length || model.hasChanges()}
                          text={model.currentTimeOffRequest?.status || 'None'}
                          items={model.timeOffRequestStatusTransitionItems}
                          classes={{
                            button: classNames(
                              classes.timeOffStatusButton,
                              classes[timeOffStatusClassName],
                              model.hasChanges() ? classes.timeOffStatusButtonHasChanges : '',
                            ),
                          }}
                        />
                      )
                    );
                  }}
                />
              </>
            );
          }}
        />
      );
    }
  },
);

const timeOffRequestContentStyles = (theme: Theme) => ({
  contentContainer: {
    display: 'flex',
    flexDirection: 'column',
  },
  form: {
    flex: '1',
    rowGap: 16,
  },
  error: {
    color: theme.palette.error.main,
  },
  timeOffBalancesTitle: {
    padding: '8px 0',
  },
  fieldContainer: {
    width: '100%',
    display: 'flex',
    // marginBottom: '15px',
    '& > div': {
      display: 'flex',
      flex: '1',
      '&:not(:last-child)': {
        marginRight: '15px',
      },
    },
  },
  timeOffStatusInputContainer: {
    paddingTop: '5px',
  },
});

interface ITimeOffRequestContentsProps {
  classes?: any;
  model: InfoPanelTimeOffRequestViewModel;
}

type TimeOffRequestContentsProps = WithStyle<ReturnType<typeof timeOffRequestContentStyles>, ITimeOffRequestContentsProps>;

export const TimeOffRequestContents = withStyles<ITimeOffRequestContentsProps>(
  timeOffRequestContentStyles,
  class extends React.PureComponent<TimeOffRequestContentsProps> {
    constructor(props: TimeOffRequestContentsProps) {
      super(props);
    }

    render() {
      const { classes, model } = this.props;

      return (
        <Observe
          render={() => (
            <>
              <div className={classes.contentContainer}>
                <FormView className={classes.form} form={model.form} onSubmit={model.actionFn('save')}>
                  <Observe
                    render={() => (
                      <div
                        className={classNames(
                          classes.fieldContainer,
                          model.isNew ? classes.timeOffStatusInputContainer : undefined,
                        )}
                      >
                        <model.form.field.allDay.Editor />
                        <model.form.field.timeOffStatus.Editor />
                      </div>
                    )}
                  />

                  <div className={classes.fieldContainer}>
                    <model.form.field.employee.Editor />
                  </div>

                  <div className={classes.fieldContainer}>
                    <model.form.field.startDate.Editor />
                    <model.form.field.endDate.Editor />
                  </div>

                  <Observe
                    render={() => {
                      return model.isAllDayRequest ? (
                        // Display units / reason editors when creating a all day request
                        <div className={classes.fieldContainer}>
                          <model.form.field.units.Editor />
                          <model.form.field.reason.Editor />
                        </div>
                      ) : null;
                    }}
                  />

                  <DetailRows model={model} />

                  <div className={classes.fieldContainer}>
                    <model.form.field.preference.Editor />
                  </div>

                  <div className={classes.fieldContainer}>
                    <model.form.field.comment.Editor />
                  </div>
                </FormView>
                <Observe
                  render={() => {
                    const { available, requested, remaining } = getAvailableBalances(model) ?? {};
                    if (available === undefined || requested === undefined || remaining === undefined) return null;
                    let balanceStatus = '';
                    let availableBalanceText = <></>;
                    availableBalanceText = (
                      <>
                        <span>
                          <b>{timeOffBalancesDialogModel?.selectedTimeOffType?.name ?? ''}</b>:&nbsp;
                        </span>
                        <span>
                          Available: <b>{available.toFixed(2)}</b>,&nbsp;
                        </span>
                        <span>
                          Requested: <b>{requested.toFixed(2)}</b>,&nbsp;
                        </span>
                        <span>
                          {remaining >= 0 ? 'Remaining' : 'Overdrawn By'}: <b>{remaining.toFixed(2)}</b>
                        </span>
                      </>
                    );
                    if (remaining < 0) {
                      balanceStatus = classes.error;
                    }

                    return model.canViewTimeOffBalances ? (
                      <>
                        <Typography variant="subtitle1" className={`${classes.timeOffBalancesTitle} ${balanceStatus}`}>
                          {availableBalanceText}
                        </Typography>
                        <Accordion
                          expanded={model.showTimeOffBalances}
                          disabled={!model.employeeId}
                          onChange={(_evt: object, expanded: boolean) => (model.showTimeOffBalances = expanded)}
                        >
                          <AccordionSummary expandIcon={<ChevronRightIcon />}>
                            <AccountBalanceIcon />
                          </AccordionSummary>
                          <AccordionDetails>{model.employeeId && <TimeOffBalancesView />}</AccordionDetails>
                        </Accordion>
                      </>
                    ) : null;
                  }}
                />
              </div>
            </>
          )}
        />
      );
    }
  },
);

export const timeOffRequestTheme = (outerTheme: Theme) => ({
  ...outerTheme,
  overrides: merge(outerTheme.overrides as any, {
    MuiAccordion: {
      root: {
        margin: '0px',
        boxShadow: 'none',
        justifyContent: 'flex-start',
        '&$expanded': {
          margin: '0px',
          minHeight: '0px',
        },
        '&$disabled': {
          backgroundColor: 'inherit',
        },
      },
    },
    MuiAccordionSummary: {
      root: {
        display: 'inline-flex',
        minHeight: '0px',
        padding: '4px 8px 4px 16px',
        borderRadius: '16px',
        marginBottom: '15px',
        justifyContent: 'flex-start',
        backgroundColor: outerTheme.palette.neutralLight.main,
        '&$expanded': {
          minHeight: '0px',
          margin: '0px 0px 15px 0px',
        },
      },
      content: {
        minHeight: '0px',
        margin: '0px',
        flexGrow: 0,
        '&$expanded': {
          margin: '0px',
        },
      },
      expandIcon: {
        marginLeft: '5px',
        marginRight: '0px',
        marginBottom: '1px',
      },
    },
    MuiAccordionDetails: {
      root: {
        flexDirection: 'column',
        padding: '0px',
      },
    },
  }),
});

const styles = (_theme: Theme) => ({});

interface IInfoPanelTimeOffRequestViewProps {
  classes?: any;
  hideActions?: boolean;
  model: InfoPanelTimeOffRequestViewModel;
}

type InfoPanelTimeOffRequestViewProps = WithStyle<ReturnType<typeof styles>, IInfoPanelTimeOffRequestViewProps>;
class InfoPanelTimeOffRequestView extends React.PureComponent<InfoPanelTimeOffRequestViewProps> {
  constructor(props: InfoPanelTimeOffRequestViewProps) {
    super(props);
  }

  render() {
    return (
      <Observe
        render={() => {
          const showPanel = this.props.model.shouldShowPanel();
          const headerTitle = !showPanel ? (
            'No time off request for this date'
          ) : (
            <TimeOffRequestHeaderTitle
              model={this.props.model}
              hideNavigation={this.props.model.isNew || timeOffModel.viewMode === 'List'}
            />
          );
          const headerOptionsTitle = 'Time Off Request Options';
          const headerOptionsItems = showPanel ? this.props.model.optionsActionMenuItems : undefined;

          return (
            <MuiThemeProvider theme={timeOffRequestTheme as DefaultTheme}>
              <InfoPanelHeader
                title={headerTitle}
                optionsMenuTitle={headerOptionsTitle}
                optionsMenuTooltip={headerOptionsTitle}
                optionsItems={headerOptionsItems}
              />
              {!showPanel ? null : (
                <InfoPanelContent>
                  <TimeOffRequestContents model={this.props.model} />
                </InfoPanelContent>
              )}
              {!this.props.hideActions && showPanel && this.props.model.actions && (
                <Observe
                  render={() => <InfoPanelFooter isDisabled={this.props.model.isDisabled} actions={this.props.model.actions} />}
                />
              )}
            </MuiThemeProvider>
          );
        }}
      />
    );
  }
}

export default withStyles<IInfoPanelTimeOffRequestViewProps>(styles, InfoPanelTimeOffRequestView);
